import React, { useState, useEffect, useCallback } from 'react';
import { tasksAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Plus,
  CheckSquare,
  Clock,
  Flag,
  MoreVertical,
  Edit,
  Trash2,
  Circle,
  CheckCircle2,
  AlertCircle,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import { format, isPast, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';

const priorityConfig = {
  low: { label: 'Basse', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400', icon: Circle },
  medium: { label: 'Moyenne', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Circle },
  high: { label: 'Haute', color: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: AlertCircle },
};

const statusConfig = {
  pending: { label: 'À faire', color: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300' },
  in_progress: { label: 'En cours', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  completed: { label: 'Terminée', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' },
};

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, pending, in_progress, completed
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    due_date: '',
    priority: 'medium',
    status: 'pending',
  });

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await tasksAPI.getTasks(filter === 'all' ? null : filter);
      setTasks(response.data.tasks);
    } catch (error) {
      console.error('Failed to load tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const openNewTask = () => {
    setTaskForm({
      title: '',
      description: '',
      due_date: '',
      priority: 'medium',
      status: 'pending',
    });
    setEditingTask(null);
    setDialogOpen(true);
  };

  const openEditTask = (task) => {
    setTaskForm({
      title: task.title,
      description: task.description || '',
      due_date: task.due_date || '',
      priority: task.priority || 'medium',
      status: task.status || 'pending',
    });
    setEditingTask(task);
    setDialogOpen(true);
  };

  const handleSaveTask = async () => {
    if (!taskForm.title.trim()) {
      toast.error('Veuillez saisir un titre');
      return;
    }

    try {
      const data = {
        ...taskForm,
        due_date: taskForm.due_date || null,
      };

      if (editingTask) {
        await tasksAPI.updateTask(editingTask.id, data);
        toast.success('Tâche modifiée');
      } else {
        await tasksAPI.createTask(data);
        toast.success('Tâche créée');
      }

      setDialogOpen(false);
      loadTasks();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleToggleStatus = async (task) => {
    const newStatus = task.status === 'completed' ? 'pending' : 'completed';
    try {
      await tasksAPI.updateTask(task.id, { ...task, status: newStatus });
      setTasks(tasks.map(t => t.id === task.id ? { ...t, status: newStatus } : t));
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await tasksAPI.deleteTask(taskId);
      toast.success('Tâche supprimée');
      loadTasks();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const formatDueDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    if (isToday(date)) return "Aujourd'hui";
    return format(date, 'd MMM', { locale: fr });
  };

  const getDueDateClass = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isPast(date) && !isToday(date)) return 'text-destructive';
    if (isToday(date)) return 'text-warning';
    return 'text-muted-foreground';
  };

  const groupedTasks = {
    pending: tasks.filter(t => t.status === 'pending'),
    in_progress: tasks.filter(t => t.status === 'in_progress'),
    completed: tasks.filter(t => t.status === 'completed'),
  };

  const filteredTasks = filter === 'all' ? tasks : tasks.filter(t => t.status === filter);

  const stats = {
    total: tasks.length,
    pending: groupedTasks.pending.length,
    in_progress: groupedTasks.in_progress.length,
    completed: groupedTasks.completed.length,
  };

  return (
    <div className="h-full flex flex-col p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] mb-2">Tâches</h1>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>{stats.pending} à faire</span>
            <span>{stats.in_progress} en cours</span>
            <span>{stats.completed} terminées</span>
          </div>
        </div>
        <Button onClick={openNewTask} data-testid="new-task-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle tâche
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 mb-6">
        {[
          { value: 'all', label: 'Toutes' },
          { value: 'pending', label: 'À faire' },
          { value: 'in_progress', label: 'En cours' },
          { value: 'completed', label: 'Terminées' },
        ].map((f) => (
          <Button
            key={f.value}
            variant={filter === f.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {/* Task list */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          {filteredTasks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <CheckSquare className="w-16 h-16 mb-4 opacity-30" />
              <p className="text-lg">Aucune tâche</p>
              <p className="text-sm mb-4">Créez votre première tâche pour commencer</p>
              <Button onClick={openNewTask}>
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle tâche
              </Button>
            </div>
          ) : (
            <div className="space-y-3 pb-6">
              {filteredTasks.map((task) => {
                const priority = priorityConfig[task.priority] || priorityConfig.medium;
                const status = statusConfig[task.status] || statusConfig.pending;
                const PriorityIcon = priority.icon;
                const isCompleted = task.status === 'completed';

                return (
                  <div
                    key={task.id}
                    className={cn(
                      "p-4 rounded-lg border border-border bg-card hover:bg-muted/30 transition-colors group",
                      isCompleted && "opacity-60"
                    )}
                    data-testid={`task-item-${task.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <button
                        className="mt-0.5 flex-shrink-0"
                        onClick={() => handleToggleStatus(task)}
                      >
                        {isCompleted ? (
                          <CheckCircle2 className="w-6 h-6 text-green-500" />
                        ) : (
                          <Circle className="w-6 h-6 text-muted-foreground hover:text-primary transition-colors" />
                        )}
                      </button>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <h3 className={cn(
                              "font-medium mb-1",
                              isCompleted && "line-through text-muted-foreground"
                            )}>
                              {task.title}
                            </h3>
                            {task.description && (
                              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                                {task.description}
                              </p>
                            )}
                            <div className="flex items-center gap-3 flex-wrap">
                              <Badge className={priority.color}>
                                <PriorityIcon className="w-3 h-3 mr-1" />
                                {priority.label}
                              </Badge>
                              <Badge variant="outline" className={status.color}>
                                {status.label}
                              </Badge>
                              {task.due_date && (
                                <span className={cn("text-sm flex items-center gap-1", getDueDateClass(task.due_date))}>
                                  <Clock className="w-3 h-3" />
                                  {formatDueDate(task.due_date)}
                                </span>
                              )}
                            </div>
                          </div>

                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="w-8 h-8 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEditTask(task)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Modifier
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDeleteTask(task.id)}
                                className="text-destructive"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>

      {/* Task Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">
              {editingTask ? 'Modifier la tâche' : 'Nouvelle tâche'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Titre *</Label>
              <Input
                placeholder="Titre de la tâche"
                value={taskForm.title}
                onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                data-testid="task-title-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Description de la tâche..."
                value={taskForm.description}
                onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label>Date d'échéance</Label>
              <Input
                type="date"
                value={taskForm.due_date}
                onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Priorité</Label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Basse</SelectItem>
                    <SelectItem value="medium">Moyenne</SelectItem>
                    <SelectItem value="high">Haute</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Statut</Label>
                <Select
                  value={taskForm.status}
                  onValueChange={(v) => setTaskForm({ ...taskForm, status: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">À faire</SelectItem>
                    <SelectItem value="in_progress">En cours</SelectItem>
                    <SelectItem value="completed">Terminée</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            {editingTask && (
              <Button
                variant="destructive"
                onClick={() => handleDeleteTask(editingTask.id)}
                className="mr-auto"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </Button>
            )}
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveTask} data-testid="save-task-btn">
              {editingTask ? 'Enregistrer' : 'Créer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
